import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, BasicButton, GhostButton } from "@certn/cascadia";
import { mq, Card, useColors, Link } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CardContent } from "./CardContent";

import { ScheduleRescreenModal } from "../../pages/person-profile/components/ScheduleRescreenModal";
import {
  useCanViewCaseScore,
  useCanViewCaseDetails,
} from "../permissions/Permissions";
import { renderStatusTag } from "../../utils/renderStatusTag";
import { TagList } from "../tag-list/TagList";

interface CaseCardProps {
  caseID: string;
  subtitle: string;
  linkedCases:
    | components["schemas"]["LinkedCase"][]
    | components["schemas"]["PersonCaseDetailsLinkedCases"][];
  status: components["schemas"]["ClientPortalOverallStatus"];
  decision: components["schemas"]["ClientPortalDecision"] | null;
  score: components["schemas"]["ClientPortalOverallScore"] | null;
  orderedDate: string;
  tags: string[];
  checks: string[];
  credentialRequest?: components["schemas"]["CredentialRequest"] | null;
  positionLocation: components["schemas"]["PositionLocation"] | null;
  rescreenFrequency: string;
  onViewCase: (arg0: string) => void;
  refreshData: () => void;
}

export function CaseCard({
  caseID,
  subtitle,
  linkedCases,
  status,
  decision,
  score,
  orderedDate,
  tags,
  checks,
  credentialRequest,
  positionLocation,
  rescreenFrequency,
  onViewCase,
  refreshData,
}: CaseCardProps) {
  const { t } = useTranslation();
  const [isCaseExpanded, setIsCaseExpanded] = useState(false);
  const [showScheduleRescreenModal, setShowScheduleRescreenModal] =
    useState(false);
  const styles = useStyles();
  const canViewScore = useCanViewCaseScore();
  const canViewCaseDetails = useCanViewCaseDetails();

  return (
    <Card type="large" className={styles.caseCard} testID="case-card">
      <div className={styles.caseCardHeader}>
        <div className={styles.headerFirstLine}>
          <div className={styles.headerInfoSection}>
            <Text
              className={css({ whiteSpace: "nowrap" })}
              style="global/heading-medium"
              testID="case-short-id-title"
            >
              {caseID.replaceAll("_", "-")}
            </Text>
            {status &&
              renderStatusTag({
                status,
                decision,
                score,
                size: "small",
                isScoreHidden: !canViewScore,
              })}
          </div>
          <div className={styles.headerButtonGroup}>
            {status.name !== "CANCELLED" &&
            status.name !== "APPLICANT_EXPIRED" &&
            !rescreenFrequency ? (
              <GhostButton
                size="small"
                leftIcon="auto_renew"
                onPress={() => setShowScheduleRescreenModal(true)}
              >
                {t("Rescreen")}
              </GhostButton>
            ) : null}
            <ScheduleRescreenModal
              onClose={() => setShowScheduleRescreenModal(false)}
              isOpen={showScheduleRescreenModal}
              caseId={caseID}
              refreshData={refreshData}
            />

            {canViewCaseDetails ? (
              <BasicButton
                size="small"
                onPress={() => onViewCase(caseID)}
                testID="view-case-button"
              >
                {t("View Case")}
              </BasicButton>
            ) : null}
          </div>
        </div>
        <div className={styles.headerSecondLine}>
          <Text color="text-weak" testID="case-card-subtitle">
            <span>{subtitle}</span>
            <span style={{ margin: "0 var(--space-1)" }}>•</span>
            <span>
              {t("Ordered")} {formatDate(orderedDate, "PP")}
            </span>
          </Text>
        </div>
        {tags && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <TagList
              tags={tags}
              capacity={3}
              hasOverflowDetails
              testID="case-card"
            />
          </div>
        )}

        {!isCaseExpanded && (
          <Link
            style={{ marginTop: "var(--space-2)" }}
            textStyle="global/body-secondary"
            textColor="text-base"
            onPress={() => setIsCaseExpanded(!isCaseExpanded)}
            aria-controls="case-details"
            aria-expanded={false}
            aria-label={t("Case Details")}
            testID="show-more-text"
          >
            {t("Show more")}
          </Link>
        )}
      </div>
      {isCaseExpanded && (
        <CardContent
          checks={checks}
          positionLocation={positionLocation}
          linkedCases={linkedCases}
          setIsCaseExpanded={setIsCaseExpanded}
          isCaseExpanded={isCaseExpanded}
          credentialRequest={credentialRequest}
          rescreenSchedule={rescreenFrequency}
          caseId={caseID}
          refreshData={refreshData}
        />
      )}
    </Card>
  );
}

function useStyles() {
  const colors = useColors();
  return {
    caseCard: css({
      display: "flex",
      flexDirection: "column",
    }),
    caseCardHeader: css({
      display: "flex",
      flexDirection: "column",
      padding: "var(--space-4)",
    }),
    headerInfoSection: css(
      {
        display: "flex",
        gap: "var(--space-2)",
        marginRight: "var(--space-1)",
      },
      mq({
        flexDirection: ["column-reverse", "row"],
        alignItems: ["normal", "center"],
        flexWrap: ["nowrap", "wrap-reverse"],
      })
    ),
    headerButtonGroup: css({
      display: "flex",
      flex: "row",
      maxHeight: "32px",
      gap: "var(--space-2)",
      whiteSpace: "nowrap",
    }),
    headerSecondLine: css({
      display: "flex",
    }),
    headerFirstLine: css({
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    }),
    caseDetails: css({
      borderTop: `solid 1px ${colors["border-base"]}`,
      padding: "var(--space-4)",
      display: "grid",
      gap: "var(--space-4)",
    }),
    caseDetailsListItem: css({
      display: "flex",
      flexDirection: "column",
    }),
    tags: css(
      {
        display: "flex",
        flexFlow: "row wrap",
        marginTop: "var(--space-1)",
        gap: "var(--space-2)",
      },
      mq({
        justifyContent: ["left", "right", "right"],
      })
    ),
  };
}
